import React, { useState, useEffect } from 'react';
import { getVehicles, getVehicleStatus, getAlarms } from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const StatCard = ({ label, value, sub, color }) => (
  <div style={{ background: color || '#f7f8fa', borderRadius:8, padding:'14px 16px' }}>
    <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>{label}</div>
    <div style={{ fontSize:24, fontWeight:500 }}>{value}</div>
    {sub && <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>{sub}</div>}
  </div>
);

const weekData = [
  { day:'Mon', alarms:4, trips:12 }, { day:'Tue', alarms:7, trips:15 },
  { day:'Wed', alarms:2, trips:18 }, { day:'Thu', alarms:9, trips:11 },
  { day:'Fri', alarms:5, trips:20 }, { day:'Sat', alarms:3, trips:8 }, { day:'Sun', alarms:1, trips:5 }
];

export default function OverviewPage() {
  const [vehicles, setVehicles] = useState([]);
  const [status, setStatus] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    const today = new Date();
    const start = new Date(today); start.setHours(0,0,0,0);
    const fmt = d => d.toISOString().replace('T',' ').substring(0,19);

    Promise.all([
      getVehicles(),
      getVehicleStatus(),
      getAlarms('', fmt(start), fmt(today))
    ]).then(([v, s, a]) => {
      setVehicles(v.vehicles || []);
      setStatus(s.infos || []);
      setAlarms(a.alarms || []);
    }).catch(e => setError('Failed to load data from CMSV6'))
    .finally(() => setLoading(false));
  }, []);

  const online = status.filter(s => s.online === 1).length;
  const offline = status.filter(s => s.online !== 1).length;
  const moving = status.filter(s => s.speed > 0).length;

  if (loading) return <div style={{ color:'#999', fontSize:13, padding:20 }}>Loading live data from CMSV6...</div>;
  if (error) return <div style={{ color:'#c0392b', fontSize:13, background:'#fdecea', padding:12, borderRadius:8 }}>{error} — Check proxy server is running and CMSV6 is reachable.</div>;

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        <StatCard label="Total vehicles" value={vehicles.length || status.length} sub="Registered" color="#E6F1FB" />
        <StatCard label="Online now" value={online} sub="Active connection" color="#EAF3DE" />
        <StatCard label="Moving" value={moving} sub="In transit" color="#FAEEDA" />
        <StatCard label="Today alerts" value={alarms.length} sub="Needs review" color="#FCEBEB" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
        <div style={{ background:'white', border:'0.5px solid #e8e8e8', borderRadius:12, padding:16 }}>
          <div style={{ fontSize:12, fontWeight:500, color:'#666', marginBottom:12 }}>Weekly alarms vs trips</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={weekData} barSize={12}>
              <XAxis dataKey="day" tick={{ fontSize:10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize:10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize:11, borderRadius:6, border:'0.5px solid #eee' }} />
              <Bar dataKey="trips" fill="#B5D4F4" radius={[3,3,0,0]} name="Trips" />
              <Bar dataKey="alarms" fill="#F09595" radius={[3,3,0,0]} name="Alarms" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background:'white', border:'0.5px solid #e8e8e8', borderRadius:12, padding:16 }}>
          <div style={{ fontSize:12, fontWeight:500, color:'#666', marginBottom:12 }}>Vehicle status breakdown</div>
          <div style={{ display:'flex', flexDirection:'column', gap:10, paddingTop:8 }}>
            {[
              { label:'Online', count: online, color:'#4CAF50', total: status.length || 1 },
              { label:'Offline', count: offline, color:'#E24B4A', total: status.length || 1 },
              { label:'Moving', count: moving, color:'#185FA5', total: status.length || 1 },
              { label:'Idle', count: Math.max(0, online - moving), color:'#EF9F27', total: status.length || 1 },
            ].map(item => (
              <div key={item.label}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                  <span style={{ fontSize:11, color:'#666' }}>{item.label}</span>
                  <span style={{ fontSize:11, fontWeight:500 }}>{item.count}</span>
                </div>
                <div style={{ background:'#f0f0f0', borderRadius:4, height:5 }}>
                  <div style={{ width:`${Math.round((item.count/item.total)*100)}%`, background:item.color, height:5, borderRadius:4, transition:'width .3s' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent alarms table */}
      <div style={{ background:'white', border:'0.5px solid #e8e8e8', borderRadius:12, padding:16 }}>
        <div style={{ fontSize:12, fontWeight:500, color:'#666', marginBottom:12 }}>Recent alerts today</div>
        {alarms.length === 0 ? (
          <div style={{ fontSize:12, color:'#aaa', textAlign:'center', padding:'20px 0' }}>No alerts today</div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr>{['Vehicle','Type','Time','Speed','Location'].map(h => (
                <th key={h} style={{ textAlign:'left', padding:'6px 8px', fontSize:11, color:'#999', borderBottom:'0.5px solid #eee', fontWeight:500 }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {alarms.slice(0,8).map((a, i) => (
                <tr key={i} style={{ borderBottom:'0.5px solid #f5f5f5' }}>
                  <td style={{ padding:'7px 8px' }}>{a.vehiIdno || a.vehicleNo || '—'}</td>
                  <td style={{ padding:'7px 8px' }}>
                    <span style={{ background:'#FCEBEB', color:'#A32D2D', fontSize:10, padding:'2px 6px', borderRadius:8 }}>{a.alarmType || 'Alert'}</span>
                  </td>
                  <td style={{ padding:'7px 8px', color:'#888' }}>{a.alarmTime || a.time || '—'}</td>
                  <td style={{ padding:'7px 8px' }}>{a.speed ? `${Math.round(a.speed)} km/h` : '—'}</td>
                  <td style={{ padding:'7px 8px', color:'#888', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.location || a.address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
